import React, { Fragment, FunctionComponent } from "react";
import styles from "./Footer.module.scss";

import { useSelector } from "react-redux";
import Anchor from "components/Anchor/Anchor";
import IconViewer from "components/IconViewer/IconViewer";
import ImageViewer from "components/ImageViewer/ImageViewer";
import Dictionary from "components/Dictionary/Dictionary";
import combineClasses from "helpers/combine-classes";

const Footer: FunctionComponent = () => {
    const settings = useSelector((state: any) => state.settings.content);

    return (
        <footer className={styles.wrapper}>
            {/* Clip Path Definition for Firefox - Shape() not supported https://caniuse.com/?search=shape*/}
            <svg
                width="0"
                height="0"
                style={{ position: "absolute", pointerEvents: "none" }}
            >
                <defs>
                    <clipPath
                        id="clip-footer-shape"
                        clipPathUnits="objectBoundingBox"
                    >
                        <path
                            d="
                M 0.2479 0.3265
                C 0.1208 0.4191 0.0297 0.4657 0 0.4773
                L 0 1
                C 0.0026 1 0.0188 0.9947 0.0634 0.9737
                C 0.119 0.9474 0.2487 0.8446 0.3197 0.8016
                C 0.396 0.7555 0.537 0.6623 0.6971 0.5899
                C 0.8251 0.532 0.9524 0.5227 1 0.5253
                L 1 0.0008
                C 0.997 0.0002 0.9776 -0.0007 0.9243 0.0008
                C 0.8578 0.0027 0.7253 0.0469 0.6567 0.0834
                C 0.588 0.1198 0.4067 0.2107 0.2479 0.3265
                Z"
                        />
                    </clipPath>
                </defs>
            </svg>

            <div className={styles.columns}>
                {settings?.footerColumns?.map((column: any, cIndex: number) => {
                    if (!column) return null;
                    return (
                        <Fragment key={`c_${cIndex}`}>
                            {column.buttons && column.buttons.length > 0 ? (
                                <div className={styles.column}>
                                    <h5 className={styles.column_headline}>{column.headline}</h5>

                                    {column.buttons?.map((button: any, bIndex: number) => {
                                        return (
                                            <Anchor
                                                key={bIndex}
                                                to={button?.link}
                                                className={styles.column_button}
                                                onClick={() => {
                                                    if (window.location.pathname === button?.link) {
                                                        window.scrollTo({ top: 0, left: 0 });
                                                    }
                                                }}
                                            >
                                                {button?.label}
                                            </Anchor>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </Fragment>
                    );
                })}

                {/* Policies */}
                <div className={styles.column}>
                    <h5 className={styles.column_headline}>{settings?.linksHeader}</h5>

                    <div className={styles.links}>
                        {settings?.footerLinks?.map((link: any, lIndex: number) => {
                            return (
                                <Anchor
                                    key={lIndex}
                                    to={link?.content?.link}
                                    className={styles.link_button}
                                >
                                    {lIndex === 0 ? (
                                        <div className={styles.link_icon}>
                                            <IconViewer name="smiley" />
                                        </div>
                                    ) : null}
                                    <span className={styles.button_label}>{link?.content?.label}</span>
                                </Anchor>
                            );
                        })}
                    </div>
                </div>

                {/* Address */}
                <div className={combineClasses([styles.column, styles.address])}>
                    {/* Headline */}
                    {settings?.footerHeadline ? <h5 className={styles.column_headline}>{settings.footerHeadline}</h5> : null}

                    {/* Address line 1 */}
                    {settings?.footerAddressLine1 ? <p className={styles.address_item}>{settings.footerAddressLine1}</p> : null}

                    {/* Address line 2 */}
                    {settings?.footerAddressLine2 ? <p className={styles.address_item}>{settings.footerAddressLine2}</p> : null}

                    {/* Phone */}
                    {settings?.footerPhone ? (
                        <a
                            className={styles.column_button}
                            href={`tel:${settings.footerPhone}`}
                        >
                            {settings.footerPhone}
                        </a>
                    ) : null}

                    {/* Email */}
                    {settings?.footerEmail ? (
                        <a
                            className={styles.column_button}
                            href={`mailto:${settings.footerEmail}`}
                        >
                            {settings.footerEmail}
                        </a>
                    ) : null}

                    {/* VAT Number */}
                    {settings?.footerVATNumber ? (
                        <p className={styles.address_item}>
                            {" "}
                            <Dictionary name="VAT_NUMBER" />
                            &nbsp;
                            {settings.footerVATNumber}
                        </p>
                    ) : null}
                </div>

                {/* Text */}
                {settings?.bodyText ? (
                    <div
                        className={styles.additional_text}
                        dangerouslySetInnerHTML={{ __html: settings?.bodyText }}
                    ></div>
                ) : null}
            </div>

            <div className={styles.middle}>
                <div className={styles.contact_wrapper}>
                    <div className={styles.social}>
                        {/* Facebook */}
                        {settings?.socialFacebookUrl ? (
                            <Anchor
                                className={styles.social_link}
                                to={settings?.socialFacebookUrl}
                            >
                                <IconViewer name="facebook" />
                            </Anchor>
                        ) : null}

                        {/* Instagram */}
                        {settings?.socialInstagramUrl ? (
                            <Anchor
                                className={styles.social_link}
                                to={settings?.socialInstagramUrl}
                            >
                                <IconViewer name="instagram" />
                            </Anchor>
                        ) : null}

                        {/* LinkedIn */}
                        {settings?.socialLinkedinUrl ? (
                            <Anchor
                                className={styles.social_link}
                                to={settings?.socialLinkedinUrl}
                            >
                                <IconViewer name="linkedin" />
                            </Anchor>
                        ) : null}

                        {/* Twitter */}
                        {settings?.socialTwitterUrl ? (
                            <Anchor
                                className={styles.social_link}
                                to={settings?.socialTwitterUrl}
                            >
                                <IconViewer name="twitter" />
                            </Anchor>
                        ) : null}

                        {/* YouTube */}
                        {settings?.socialYoutubeUrl ? (
                            <Anchor
                                className={styles.social_link}
                                to={settings?.socialYoutubeUrl}
                            >
                                <IconViewer name="youtube" />
                            </Anchor>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className={styles.media_container}>
                <div className={styles.media_wrapper}>
                    <div className={styles.logos}>
                        {settings?.footerLogos?.map((logo: any, loIndex: number) => {
                            if (!logo) return null;
                            return (
                                <Fragment key={loIndex}>
                                    {typeof logo.media !== "undefined" ? (
                                        <div
                                            key={loIndex}
                                            className={styles.logo_wrapper}
                                        >
                                            <a
                                                className={styles.logo_link}
                                                href={logo.externalLink}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <ImageViewer
                                                    src={logo.media?.url || logo.media}
                                                    aspectRatioX={logo.media?.width}
                                                    aspectRatioY={logo.media?.height}
                                                    extensionType={logo.media?.extension}
                                                />
                                            </a>
                                        </div>
                                    ) : null}
                                </Fragment>
                            );
                        })}
                    </div>

                    <div className={styles.images}>
                        {settings?.footerImages?.map((image: any, index: number) => {
                            if (!image) return null;
                            return (
                                <Fragment key={index}>
                                    {typeof image.media !== "undefined" ? (
                                        <div
                                            key={index}
                                            className={styles.image_wrapper}
                                        >
                                            <ImageViewer
                                                src={image.media?.url || image.media}
                                                aspectRatioX={image.media?.width}
                                                aspectRatioY={image.media?.height}
                                                extensionType={image.media?.extension}
                                            />
                                        </div>
                                    ) : null}
                                </Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
